import React, { useState, useEffect, useRef, useMemo } from "react";
import { withRouter } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import { ROUTE_BROKER_DASHBOARD } from "../../constants/routes";
import styled from "styled-components";
import { ToastError } from "../../components/Global/Toasts/Toasty";
import {
  Grid,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridColumnReorderEvent,
  GridColumnProps
} from "@progress/kendo-react-grid";
import {
  process as processData,
  State as DataState,
  SortDescriptor,
  GroupDescriptor
} from "@progress/kendo-data-query";
import {
  ExcelExport
} from "@progress/kendo-react-excel-export";
import "./kendo-theme-default.css";

import { CircularProgress } from "@material-ui/core";

import PageHeader, { StyledButton } from "./PageHeader";
import DealsTable from "./DealsTable";
import { tableColumns, defaultColumns, ColumnInterface } from "./tableColumns";

import { compose } from "redux";
import { connect } from "react-redux";
import { getBrokerDeals } from "./brokerDashboard.store";
import { clearCachedBrokerDealsData } from "./cache";
import { BrokerDeal } from "../../model/BrokerDeal";
import exportBrokerDataToExcel from "./excelExport";

import UserAuthentication from "../../model/UserAuthentication";
import {
  ViewConfiguration,
  getViewConfiguration,
  saveViewConfiguration
} from "./viewConfigurationStorage";

import { useFlags } from "launchdarkly-react-client-sdk";

const EmptyStateParagraph = styled.p`
  margin: 100px 0;
  text-align: center;
  color: #84838d;
  font-weight: 500;
`;

const LoadingIndicatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 200px auto;
  width: 200px;
  text-align: center;
`;

const FilterResetButton = styled(StyledButton)`
  display: block;
  margin: 20px auto;
`;

const LoadingIndicator = () => {
  return (
    <LoadingIndicatorContainer>
      <CircularProgress />
      <p>Preparing your deals. This may take a moment.</p>
    </LoadingIndicatorContainer>
  );
};

interface BrokerDashboardProps {
  brokerDeals: BrokerDeal[];
  getBrokerDeals: () => void;
  isLoading: boolean;
  authentication: UserAuthentication;
  history: RouteComponentProps["history"];
}

const BrokerDashboard = ({
  brokerDeals,
  getBrokerDeals: getDeals,
  isLoading,
  authentication,
  history
}: BrokerDashboardProps) => {
  useEffect(() => {
    if (history.location.pathname === ROUTE_BROKER_DASHBOARD) {
      getDeals();
    }
  }, [getDeals, history]);

  const { us998BrokerDealsDashboardPaginationUi: shouldPaginateTableData, us1499BrokerCompaniesUsingBrokerDealsListPage: brokerCompanyIds } = useFlags();
  const isBrokerCompany = brokerCompanyIds?.includes(authentication.user.contact.companyId);

  const defaultPagination = shouldPaginateTableData
    ? { take: 100, skip: 0 }
    : {};

  const defaultSortDescriptors: SortDescriptor[] = [
    { field: "clientName", dir: "asc" },
    { field: "dealName", dir: "asc" },
  ];
  const defaultGroups: GroupDescriptor[] = [
    {
      field: "clientName"
    }
  ];
  const defaultDataState = {
    sort: defaultSortDescriptors,
    group: defaultGroups,
    ...defaultPagination,
  };
  const [dataState, setDataState] = useState<DataState>(defaultDataState);

  const [columns, setColumns] = useState<ColumnInterface[]>(defaultColumns);

  const [orderColumns, setOrderColumns] = useState<GridColumnProps[]>([]);

  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const exportObject = useRef<ExcelExport | null>(null);
  const dataGrid = useRef<Grid | null>(null);
    
  const clearCacheAndReloadData = () => {
    clearCachedBrokerDealsData();
    getDeals();
  };

  const exportData = () => {
    if (!exportObject.current || !dataGrid.current) {
      ToastError("A problem occurred exporting the selected deals.");
      return;
    }
    const data = processData(searchFilteredData, {
      sort: dataState.sort,
      filter: dataState.filter,
    }); //add filters check 'process' in kendo-data-query
    exportBrokerDataToExcel(
      data,
      exportObject.current,
      orderColumns.length ? orderColumns : dataGrid.current.columns
    );
  };

  const onDataStateChange = (event: GridDataStateChangeEvent) => {
    const config: ViewConfiguration = {
      dataState: event.dataState,
    };
    saveViewConfiguration(config);
    setDataState(event.dataState);
  };

  const didChangeVisibleColumns = (newColumns: ColumnInterface[]) => {
    const columnIds = newColumns.map((column) => column.field ?? "");
    const config = { columns: columnIds };
    saveViewConfiguration(config);
    setColumns(newColumns);
  };

  const didReorderGridColumns = (event: GridColumnReorderEvent) => {
    const newColumns = event.columns.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
    const columnIds = newColumns.map((column) => column.field ?? "");
    const config = { columns: columnIds };
    saveViewConfiguration(config);
    setOrderColumns(event.columns);
    setColumns(newColumns);
  };

  const onGroupExpandChange = (event: GridExpandChangeEvent) => {
    let groups = [...expandedGroups];
    if (groups.includes(event.dataItem.value)) {
      groups = groups.filter((group) => group !== event.dataItem.value);
    } else {
      groups.push(event.dataItem.value);
    }
    setExpandedGroups(groups);
  };

  const clearFilters = () => {
    const state = { ...dataState, filter: undefined };
    const viewConfig = { dataState: state };
    saveViewConfiguration(viewConfig);
    setDataState(state);
  };

  const getColumnsMatchingIds = (columnIds: string[]) => {
    return tableColumns.filter((column) =>
      columnIds.includes(column.field || "")
    );
  };

  useEffect(() => {
    const { columns: savedColumns, dataState: savedDataState } =
      getViewConfiguration();
    if (savedColumns && savedColumns.length > 0) {
      const matchingColumns = getColumnsMatchingIds(savedColumns);
      matchingColumns.sort(
        (a, b) =>
          savedColumns.indexOf(a.field || "") -
          savedColumns.indexOf(b.field || "")
      );
      const newColumns =
        matchingColumns.length > 0 ? matchingColumns : tableColumns;
      setColumns(newColumns);
    }

    setDataState(savedDataState ?? defaultDataState);
  }, [shouldPaginateTableData]);

  const [searchText, setSearchText] = useState<string>("");
  const [searchFilteredData, setSearchFilteredData] = useState<BrokerDeal[]>(
    brokerDeals ?? []
  );

  useEffect(() => {
    if (searchText.length < 1 || !brokerDeals) {
      setSearchFilteredData(brokerDeals ?? []);
      return;
    }

    const filtered = brokerDeals.filter((deal) => {
      return (
        deal.dealName?.toLowerCase().indexOf(searchText?.toLowerCase()) !== -1
        ||
        (deal.clientName?.toLowerCase().indexOf(searchText?.toLowerCase()) !== -1 && deal.clientName)
      );
    });
    setSearchFilteredData(filtered);
    setDataState({ ...dataState, skip: 0 });
  }, [brokerDeals, searchText]);

  const filteredData = useMemo(() => processData(searchFilteredData, dataState), [searchFilteredData, dataState]);
  const groupedData = useMemo(() => processData(searchFilteredData, dataState).data.map(item => {
    return {
      ...item,
      expanded: expandedGroups.includes(item.value)
    };
  }), [searchFilteredData, dataState, expandedGroups]);

  const allGroups = groupedData.map((group) => group.value);
  const areAllGroupsExpanded =
    allGroups.every((group) => expandedGroups.includes(group)) || false;
  const shouldShowExpandAllButton = (dataState.group?.length || 0) > 0;

  const expandAllChanged = () => {
    if (!dataState.group) {
      return;
    }
    setExpandedGroups(areAllGroupsExpanded ? [] : allGroups);
  };

  const renderPageHeader = () => {
    return (
      <PageHeader
        areAllGroupsExpanded={areAllGroupsExpanded}
        areAnyFiltersApplied={!!dataState.filter}
        expandAllGroups={expandAllChanged}
        refreshData={clearCacheAndReloadData}
        exportData={exportData}
        isExportEnabled={searchFilteredData.length > 0}
        onClearFilters={clearFilters}
        searchTextDidChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchText(e.target.value)
        }
        shouldShowExpandAllButton={shouldShowExpandAllButton}
        shouldShowRefreshButton={authentication.isSystemAdmin}
      />
    );
  };

  const setIsBrokerCompany = (items: any[]): void => {
    for (const item of items) {
      if (Array.isArray(item)) {
        setIsBrokerCompany(item);
      } else if (item.items && Array.isArray(item.items)) {
        setIsBrokerCompany(item.items);
      } else {
        item.isBrokerCompany = isBrokerCompany;
      }
    }
  };

  const renderMainContent = () => {
    if (groupedData.length < 1) {
      if (dataState.filter) {
        return (
          <EmptyStateParagraph>No Deals matched your selected filters.<FilterResetButton onClick={clearFilters}>Clear Filters</FilterResetButton></EmptyStateParagraph>
        );
      }
      else {
        return <EmptyStateParagraph>No available Deal data.</EmptyStateParagraph>;
      }
    } else {

      setIsBrokerCompany(groupedData);

      return (
        <ExcelExport data={searchFilteredData} fileName="Deals.xlsx" ref={exportObject}>
          <DealsTable
            data={groupedData}
            totalDataLength={filteredData.total}
            dataState={dataState}
            allColumns={tableColumns}
            visibleColumns={columns}
            onDataStateChange={onDataStateChange}
            onColumnReorder={didReorderGridColumns}
            onGroupExpandChange={onGroupExpandChange}
            onColumnChange={didChangeVisibleColumns}
            paginateData={shouldPaginateTableData}
            ref={dataGrid}
            isSystemAdmin={authentication.isSystemAdmin}
          />
        </ExcelExport>
      );
    }
  };

  return (
    <>
      {renderPageHeader()}
      {isLoading ? <LoadingIndicator /> : renderMainContent()}
    </>
  );
};

const mapStateToProps = ({ brokerDeals, authentication }: { brokerDeals: BrokerDeal[], authentication: UserAuthentication }) => ({ ...brokerDeals, authentication });

export default compose(withRouter, connect(mapStateToProps, { getBrokerDeals }))(BrokerDashboard);
